// src/features/statusdiario/StatusDiarioContainer.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import StatusDiarioPage from "./StatusDiarioPage";
import "./StatusDiarioPage.css";
import "./statusdiario.flags.css";

import { getStatusDiario } from "./api";
import {
  RT,
  RT_META,
  on,
  off,
  connectRealtime,
  setScopedRoom,
  joinRoom,
  leaveRoom,
  onRealtimeMeta,
  offRealtimeMeta,
} from "../../api/realtime";
import { makeStatusDayRoom, makeWorkflowAllRoom } from "../../api/realtime.rooms";

const SD_RT_DEBUG =
  (process.env.REACT_APP_SD_RT_DEBUG || "0") === "1" ||
  (typeof window !== "undefined" && window.localStorage?.getItem("sd.rt.debug") === "1");

function dlog(...args) {
  if (SD_RT_DEBUG) console.debug(...args);
}

function todayYMD_BR() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** normaliza para comparação robusta */
const normKey = (v) =>
  String(v ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");

function metaKind(row) {
  const m = normKey(row?.meta ?? row?.tipo_meta ?? row?.meta_pos ?? "");
  if (m === "pos" || m === "p" || m === "linhapos") return "pos";
  return "meta";
}

const pickAny = (obj, keys, fallback = undefined) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== "";

const showQtyProg = (v) => (v === null || v === undefined || String(v).trim() === "" ? "—" : String(v));
const showQtyReal = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  return s === "" ? "" : s;
};

function inferDayFromPayload(payload) {
  const obj = payload?.payload ?? payload?.raw ?? payload;

  const cand =
    obj?.day ||
    obj?.data ||
    obj?.row?.day ||
    obj?.row?.data ||
    obj?.row?.w3_data ||
    obj?.row?.w2_data ||
    null;

  if (!cand) return null;
  const s = String(cand).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{4}-\d{2}-\d{2})\s+/);
  return m ? m[1] : null;
}

function extractRealtimeRow(payload) {
  const obj = payload?.payload ?? payload?.raw ?? payload;
  if (obj?.row && typeof obj.row === "object") return obj.row;
  if (obj?.data && typeof obj.data === "object" && (obj.data.codigo_unico || obj.data.id)) return obj.data;
  if (obj && typeof obj === "object" && (obj.codigo_unico || obj.id)) return obj;
  return null;
}

function extractRealtimeCode(payload) {
  const row = extractRealtimeRow(payload);
  return (
    row?.codigo_unico ||
    row?.id_display ||
    row?.codigo ||
    row?.id ||
    payload?.codigo_unico ||
    payload?.id ||
    null
  );
}

function isPatchableStatusDiarioRow(row) {
  if (!row || typeof row !== "object") return false;

  const hasStatusShape =
    isFilled(row?.key) ||
    isFilled(row?.atividade_exibicao) ||
    isFilled(row?.horario_exibicao) ||
    isFilled(row?.quantidade_exibicao);

  if (!hasStatusShape) return false;

  const coreMarkers = [
    "atividade_prog",
    "horario_prog",
    "quantidade_prog",
    "previsao_inicio_intervalo",
    "gerencia",
    "ativo",
  ];

  return coreMarkers.some((key) => {
    const value = row?.[key];
    return value !== undefined && value !== null && String(value).trim() !== "";
  });
}

/** ✅ regra oficial: status_wf3 manda nas flags */
function flagsFromRow(it) {
  // 0) se backend já trouxe boolean explícito, usa direto
  const bReprog = it?.is_reprogramado === true;
  const bFinal = it?.is_finalizado === true;
  if (bReprog) return { reprog: true, final: false };
  if (bFinal) return { reprog: false, final: true };

  // 1) status_wf3 (fonte principal)
  const sw = normKey(pickAny(it, ["status_wf3", "statuswf3", "status_wf_3", "wf3_status", "status"], ""));
  if (sw.includes("reprogram")) return { reprog: true, final: false };
  if (sw.includes("finaliz")) return { reprog: false, final: true };

  // 2) fallback legado
  const sp = normKey(pickAny(it, ["status_programacaod1", "status_programacao_d1", "status_programacao", "status_d1"], ""));
  const sf = normKey(pickAny(it, ["status_finalizacao", "finalizacao", "status_fim"], ""));

  if (sp.includes("reprogram")) return { reprog: true, final: false };
  if (sf.includes("finaliz")) return { reprog: false, final: true };

  return { reprog: false, final: false };
}

/** aplica classes sem duplicar e com prioridade */
function applyFlagClasses(base, flags) {
  let cls = String(base || "").trim();
  cls = cls.replace(/\bsd-flag--reprogramar\b/g, "").replace(/\bsd-flag--finalizado\b/g, "").trim();

  if (flags.reprog) return `${cls} sd-flag--reprogramar`.trim();
  if (flags.final) return `${cls} sd-flag--finalizado`.trim();
  return cls;
}

/** ✅ cria uma "baseKey" forte e depois resolve duplicadas com sufixo determinístico */
function buildStableKey({ dayKey, it, idx }) {
  const stableId = pickAny(it, [
    "id_programacao",
    "programacao_id",
    "idProgramacao",
    "wf3_id",
    "id_wf3",
    "w3_id",
    "wf2_id",
    "id_wf2",
    "w2_id",
    "id", // se for PK
  ]);

  // ⚠️ codigo_unico pode repetir (seu erro mostrou isso)
  const codigo = pickAny(it, ["codigo_unico", "id_display", "codigo", "codigoUnico"]);

  const ger = pickAny(it, ["gerencia", "gerencia_nome", "up"], "");
  const ativo = pickAny(it, ["ativo", "equipamento", "equipamento_nome"], "");
  const hora = pickAny(it, ["horario_prog", "horario_do_intervalo", "hora_prog", "horario_real"], "");
  const atv = pickAny(it, ["atividade_prog", "atividade", "atividade_real"], "");
  const kmIni = pickAny(it, ["kmInicio", "km_inicio", "km_inicial", "kminicio_real", "km_inicio_real"], "");
  const kmFim = pickAny(it, ["kmFinal", "km_final", "km_fim"], "");

  // ✅ prioridade 1: ID real do banco (normalmente único)
  if (stableId != null && String(stableId).trim() !== "") {
    return `${dayKey}::${String(stableId).trim()}`;
  }

  // ✅ prioridade 2: assinatura forte (quase impossível repetir sem ser linha realmente “gêmea”)
  const sig = [codigo || "sem_codigo", ger, ativo, hora, atv, kmIni, kmFim].map((x) => String(x ?? "").trim()).join("::");

  // fallback final: ainda garante algo
  return `${dayKey}::${sig || `idx${idx}`}`;
}

/** garante unicidade final (se ainda assim duplicar, adiciona __2, __3 na ordem do payload) */
function makeKeysUniqueStable(list) {
  const seen = new Map(); // baseKey -> count
  return list.map((row) => {
    const base = String(row.key_base || row.key || "").trim();
    const n = (seen.get(base) || 0) + 1;
    seen.set(base, n);
    const key = n === 1 ? base : `${base}__${n}`;
    return { ...row, key };
  });
}

export default function StatusDiarioContainer({ onBack }) {
  const [dayKey, setDayKey] = useState(todayYMD_BR());
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const abortRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const dayRef = useRef(dayKey);

  const rtTimerRef = useRef(null);

  useEffect(() => {
    dayRef.current = dayKey;
  }, [dayKey]);

  const upsertRawRow = useCallback((incoming) => {
    if (!incoming || typeof incoming !== "object") return;
    const nextRow = {
      ...incoming,
      meta: String(incoming.meta ?? "SIM").trim() || "SIM",
    };
    const key = String(extractRealtimeCode(nextRow) ?? "").trim();
    if (!key) return;

    setRaw((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const idx = arr.findIndex((item) => String(extractRealtimeCode(item) ?? "").trim() === key);
      if (idx >= 0) {
        const next = arr.slice();
        next[idx] = { ...(arr[idx] || {}), ...nextRow };
        return next;
      }
      return [nextRow, ...arr];
    });
  }, []);

  const removeRawByCode = useCallback((codigoOrId) => {
    const key = String(codigoOrId ?? "").trim();
    if (!key) return;
    setRaw((prev) =>
      (Array.isArray(prev) ? prev : []).filter(
        (item) => String(extractRealtimeCode(item) ?? "").trim() !== key
      )
    );
  }, []);

  const fetchDay = useCallback(async ({ day, silent } = {}) => {
    const targetDay = day || dayRef.current;
    const seq = ++fetchSeqRef.current;

    abortRef.current?.abort?.();
    const ac = new AbortController();
    abortRef.current = ac;

    dlog("[SD] fetchDay()", { day: targetDay, silent, seq });

    try {
      if (!silent) setLoading(true);
      if (!silent) setErr("");

      const resp = await getStatusDiario({
        day: targetDay,
        signal: ac.signal,
      });

      if (seq !== fetchSeqRef.current) return;

      const rows = resp?.rows ?? resp?.data ?? resp?.result ?? (Array.isArray(resp) ? resp : []);
      setRaw(Array.isArray(rows) ? rows : []);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      if (seq !== fetchSeqRef.current) return;

      if (!silent) {
        setErr(e?.response?.data?.error || e?.message || "Erro ao buscar Status Diário");
        setRaw([]);
      }
    } finally {
      if (seq !== fetchSeqRef.current) return;
      if (!silent) setLoading(false);
    }
  }, []);

  // ✅ conecta realtime 1x
  useEffect(() => {
    connectRealtime()
      .then(() => dlog("[SD][RT] connect ok"))
      .catch((e) => dlog("[SD][RT] connect falhou", e?.message || e));
  }, []);

  useEffect(() => {
    const room = makeWorkflowAllRoom("wf3");
    if (room) joinRoom(room);
    return () => {
      if (room) leaveRoom(room);
    };
  }, []);

  // ✅ troca room do dia sem empilhar
  useEffect(() => {
    const room = makeStatusDayRoom(dayKey);
    setScopedRoom("status:day:", room);
    dlog("[SD][RT] scoped room =", room);
  }, [dayKey]);

  // ✅ cleanup só no unmount
  useEffect(() => {
    return () => {
      setScopedRoom("status:day:", null);
      abortRef.current?.abort?.();
      if (rtTimerRef.current) clearTimeout(rtTimerRef.current);
      rtTimerRef.current = null;
    };
  }, []);

  // ✅ quando troca dia: limpa + busca
  useEffect(() => {
    dayRef.current = dayKey;

    if (rtTimerRef.current) {
      clearTimeout(rtTimerRef.current);
      rtTimerRef.current = null;
    }

    setRaw([]);
    setErr("");
    fetchDay({ day: dayKey, silent: false });

    return () => {
      abortRef.current?.abort?.();
    };
  }, [dayKey, fetchDay]);

  const applyRealtimePatch = useCallback(
    (payload) => {
      const row = extractRealtimeRow(payload);
      const codigo = extractRealtimeCode(payload);
      const current = dayRef.current;
      const pDay = inferDayFromPayload(payload);

      if (!codigo) return false;

      if (row && typeof row === "object") {
        if (!isPatchableStatusDiarioRow(row)) return false;
        if (pDay && pDay === current) {
          upsertRawRow(row);
        } else {
          removeRawByCode(codigo);
        }
        return true;
      }

      if (pDay && pDay !== current) {
        removeRawByCode(codigo);
        return true;
      }

      return false;
    },
    [removeRawByCode, upsertRawRow]
  );

  // ✅ eventos -> patch local; refresh silencioso só como fallback
  const scheduleRefreshIfRelevant = useCallback(
    (eventName, payload) => {
      if (applyRealtimePatch(payload)) return;

      const pDay = inferDayFromPayload(payload);
      const current = dayRef.current;

      dlog("[SD][RT] evento", eventName, { pDay, current });

      let shouldRefresh = false;

      if (eventName === RT.STATUS_DIARIO_INVALIDATE) {
        shouldRefresh = true;
      } else if (eventName === RT.WF3_CHANGED) {
        shouldRefresh = !pDay || pDay === current;
      } else {
        shouldRefresh = pDay && pDay === current;
      }

      if (!shouldRefresh) return;

      if (rtTimerRef.current) clearTimeout(rtTimerRef.current);
      rtTimerRef.current = setTimeout(() => {
        fetchDay({ day: current, silent: true });
      }, 180);
    },
    [applyRealtimePatch, fetchDay]
  );

  useEffect(() => {
    const hInvalidate = (p) => scheduleRefreshIfRelevant(RT.STATUS_DIARIO_INVALIDATE, p);
    const hW3Changed = (p) => scheduleRefreshIfRelevant(RT.WF3_CHANGED, p);
    const hReconnect = () => {
      const current = dayRef.current;
      dlog("[SD][RT] reconnect -> resync", current);
      fetchDay({ day: current, silent: true });
    };

    on(RT.STATUS_DIARIO_INVALIDATE, hInvalidate);
    on(RT.WF3_CHANGED, hW3Changed);
    onRealtimeMeta(RT_META.RECONNECTED, hReconnect);

    return () => {
      off(RT.STATUS_DIARIO_INVALIDATE, hInvalidate);
      off(RT.WF3_CHANGED, hW3Changed);
      offRealtimeMeta(RT_META.RECONNECTED, hReconnect);
    };
  }, [fetchDay, scheduleRefreshIfRelevant]);

  /** ✅ monta rows finais (com KEY ÚNICA) */
  const rows = useMemo(() => {
    if (!raw?.length) return [];

    const base = raw
      .filter((it) => metaKind(it) === "meta")
      .map((it, idx) => {
        const flags = flagsFromRow(it);

        const baseRowClass = String(pickAny(it, ["rowClass", "row_class"], "")).trim();
        const rowClass = applyFlagClasses(baseRowClass, flags);

        const tempoProgPOS = pickAny(it, ["pos_tempo_intervalo"]);
        const tempoProgMETA = pickAny(it, [
          "meta_tempo_intervalo",
          "tempo_do_intervalo",
          "tempo_intervalo",
          "tempo_intervalo_prog",
          "tempo_prog",
        ]);
        const tempoProg = tempoProgPOS ?? tempoProgMETA ?? "";

        const localPartidaReal = pickAny(it, ["local_partida_real", "local_partida", "sb_origem_real", "sborigem_real"]);
        const sbTrabalhoReal = pickAny(it, ["sb_trabalho_real", "sb_de_trabalho_real", "sbdetrabalho_real", "sb_trabalho"]);
        const kmReal = pickAny(it, ["km_real", "kminicio_real", "km", "km_inicio_real"]);

        const detalhamento =
          (pickAny(it, ["detalhamento_real", "observacao_real", "observacoes_real", "descricao_real", "detalhamento"]) || "")
            .toString()
            .trim() || "—";

        const qtdProgRaw = pickAny(it, ["qtd", "quantidade", "quantidade_prog", "quantidade_planejada", "qtd_planejada"]);
        const qtdRealRaw = pickAny(it, ["quantidade_real", "qtd_real", "quantidade_executada", "qtd_executada"]);

        const codigo = pickAny(it, ["codigo_unico", "id_display", "codigo", "id"]);
        const status_wf3 = String(pickAny(it, ["status_wf3", "statuswf3", "wf3_status", "status"], "")).trim();

        const key_base = buildStableKey({ dayKey, it, idx });

        return {
          // ⚠️ key final vai ser ajustada depois para evitar duplicadas
          key_base,
          rawIndex: idx,

          id_display: codigo || (it.id != null ? String(it.id) : "—"),
          gerencia: pickAny(it, ["gerencia", "gerencia_nome", "up"], "—"),
          ativo: pickAny(it, ["ativo", "equipamento", "equipamento_nome"], "—"),

          atividade_prog: pickAny(it, ["atividade_prog", "atividade"], "—"),
          horario_prog: (() => {
            const v = pickAny(it, ["horario_prog", "horario_do_intervalo", "hora_prog"]);
            return v == null || v === "" ? "—" : String(v);
          })(),

          tempo_intervalo: tempoProg ? String(tempoProg) : "—",
          tempo_do_intervalo: tempoProg || "",
          meta_tempo_intervalo: tempoProg || "",

          atividade_real: (() => {
            const v = pickAny(it, ["atividade_real"]);
            return v == null || v === "" ? "—" : String(v);
          })(),
          horario_real: (() => {
            const v = pickAny(it, ["horario_real"]);
            return v == null || v === "" ? "—" : String(v);
          })(),

          primeiro_inicio: pickAny(it, ["primeiro_inicio", "primeiro_inicio_at"]),
          primeiro_fim: pickAny(it, ["primeiro_fim"]),
          segundo_inicio: pickAny(it, ["segundo_inicio"]),
          segundo_fim: pickAny(it, ["segundo_fim"]),
          terceiro_inicio: pickAny(it, ["terceiro_inicio"]),
          terceiro_fim: pickAny(it, ["terceiro_fim"]),

          local_partida: localPartidaReal ?? "—",
          sb_trabalho: sbTrabalhoReal ?? "—",
          km: kmReal ?? "—",

          quantidade_prog: showQtyProg(qtdProgRaw),
          quantidade_real: showQtyReal(qtdRealRaw),

          previsao_inicio_intervalo: pickAny(it, ["previsao_inicio_intervalo"], "—"),

          detalhamento,

          status_wf3,
          is_reprogramado: flags.reprog,
          is_finalizado: flags.final,

          rowClass,

          // ✅ TIMER: dados calculados pelo backend (inicial)
          // Frontend usa timer_calculated_at para atualizar display localmente
          timer_status: it.timer_status ?? "waiting",
          timer_remaining_signed: it.timer_remaining_signed ?? null,
          timer_meta_seconds: it.timer_meta_seconds ?? null,
          timer_calculated_at: it.timer_calculated_at ?? Date.now(),
        };
      });

    const unique = makeKeysUniqueStable(base);

    // ✅ debug: se sobrar duplicada (não deveria)
    if (SD_RT_DEBUG) {
      const count = new Map();
      for (const r of unique) count.set(r.key, (count.get(r.key) || 0) + 1);
      const dups = Array.from(count.entries()).filter(([, n]) => n > 1);
      if (dups.length) console.warn("[SD] KEYS DUPLICADAS AINDA:", dups);
    }

    return unique;
  }, [raw, dayKey]);

  return (
    <>
      {err ? <div style={{ padding: 8, color: "#b91c1c", fontWeight: 700 }}>{err}</div> : null}

      <StatusDiarioPage
        key={dayKey}
        onBack={onBack}
        rows={rows}
        dayKey={dayKey}
        setDayKey={setDayKey}
        loading={loading}
      />
    </>
  );
}
